import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { api, type UserNotification } from '../lib/api'

export function timeAgo(iso: string | null) {
  if (!iso) return ''
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'vừa xong'
  if (s < 3600) return `${Math.floor(s / 60)} phút trước`
  if (s < 86400) return `${Math.floor(s / 3600)} giờ trước`
  if (s < 86400 * 30) return `${Math.floor(s / 86400)} ngày trước`
  return new Date(iso).toLocaleDateString('vi-VN')
}

export function useOpenNotification() {
  const navigate = useNavigate()
  return useCallback(
    async (n: UserNotification) => {
      if (!n.read) await api.readNotification(n.id).catch(() => null)
      if (n.to) navigate(n.to)
    },
    [navigate],
  )
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<UserNotification[]>([])
  const [unread, setUnread] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const openItem = useOpenNotification()

  const load = useCallback(() => {
    api
      .notifications(1, 8)
      .then((r) => {
        setItems(r.items)
        setUnread(r.unread)
      })
      .catch(() => null)
  }, [])

  useEffect(() => {
    load()
    const t = window.setInterval(load, 60000)
    return () => window.clearInterval(t)
  }, [load])

  useEffect(() => setOpen(false), [location.pathname, location.search])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  async function readAll() {
    await api.readNotification('all').catch(() => null)
    load()
  }

  return (
    <div className={`bell${open ? ' open' : ''}`} ref={ref}>
      <button
        type="button"
        className="nav-ghost btn-reset bell-btn"
        aria-label={unread ? `Thông báo, ${unread} chưa đọc` : 'Thông báo'}
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v)
          if (!open) load()
        }}
      >
        <i className="fa-solid fa-bell" aria-hidden />
        {unread ? <span className="bell-count">{unread > 99 ? '99+' : unread}</span> : null}
      </button>
      {open ? (
        <div className="bell-menu" role="menu">
          <div className="bell-head">
            <strong>Thông báo</strong>
            {unread ? (
              <button type="button" className="btn-reset bell-readall" onClick={readAll}>
                Đánh dấu đã đọc
              </button>
            ) : null}
          </div>
          {!items.length ? <p className="muted bell-empty">Chưa có thông báo.</p> : null}
          <ul>
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className={`btn-reset bell-item${n.read ? '' : ' unread'}`}
                  onClick={async () => {
                    setOpen(false)
                    await openItem(n)
                    load()
                  }}
                >
                  <span>{n.text}</span>
                  <time>{timeAgo(n.created_at)}</time>
                </button>
              </li>
            ))}
          </ul>
          <Link className="bell-all" to="/account/thong-bao">
            Xem tất cả
          </Link>
        </div>
      ) : null}
    </div>
  )
}
